import { Marker, Point, Map } from 'mapbox-gl';
import EdgeMarker from '@/components/atoms/EdgeMarker.vue';

interface EdgeMarkerProperties {
  x: number;
  y: number;
  classes: string[];
}

/* eslint-disable no-underscore-dangle */
export default class MapboxEdgeMarker extends Marker {
  // Edge offset for edge marker
  private static readonly edgeOffset = 28; // 3.5rem

  private static readonly buttonOffset = 56; // 7rem;

  private markerEl: HTMLElement;

  private edgeMarkerEl: HTMLElement;

  private pos!: Point;

  private mapContainer!: HTMLElement;

  private safeAreaInsetTop!: number;

  private safeAreaInsetRight!: number;

  private safeAreaInsetBottom!: number;

  private safeAreaInsetLeft!: number;

  // declare property from base class Marker
  _map!: Map;

  constructor(markerEl: HTMLElement, edgeMarker: EdgeMarker) {
    super(markerEl);
    this.edgeMarkerEl = edgeMarker.$el as HTMLElement;
    this.markerEl = markerEl;
    this.getSafeAreaInsetValues();
    window.addEventListener('orientationchange', this.getSafeAreaInsetValues);
    window.addEventListener('resize', this.getSafeAreaInsetValues);
  }

  public destruct() {
    window.removeEventListener(
      'orientationchange',
      this.getSafeAreaInsetValues
    );
    window.removeEventListener('resize', this.getSafeAreaInsetValues);
  }

  private getSafeAreaInsetValues = () => {
    this.safeAreaInsetTop = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--safeAreaInsetTop'
      ),
      10
    );
    this.safeAreaInsetRight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--safeAreaInsetRight'
      ),
      10
    );
    this.safeAreaInsetBottom = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--safeAreaInsetBottom'
      ),
      10
    );
    this.safeAreaInsetLeft = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--safeAreaInsetLeft'
      ),
      10
    );
  };

  private isPointOffscreen(point: Point): boolean {
    return (
      point.x < 0 - this.markerEl.clientWidth / 2 ||
      point.x > this.mapContainer.clientWidth + this.markerEl.clientWidth / 2 ||
      point.y < 0 - this.markerEl.clientHeight / 2 ||
      point.y > this.mapContainer.clientHeight + this.markerEl.clientHeight / 2
    );
  }

  private calculateEdgeMarkerProperties(
    markerPosition: Point
  ): EdgeMarkerProperties {
    // Figure out which side the marker is furthest away from
    const topDistance = 0 - markerPosition.y;
    const bottomDistance = markerPosition.y - this.mapContainer.clientHeight;
    const leftDistance = 0 - markerPosition.x;
    const rightDistance = markerPosition.x - this.mapContainer.clientWidth;
    const sides: { side: string; distance: number }[] = [
      {
        side: 'top',
        distance: topDistance,
      },
      {
        side: 'bottom',
        distance: bottomDistance,
      },
      {
        side: 'left',
        distance: leftDistance,
      },
      {
        side: 'right',
        distance: rightDistance,
      },
    ];
    sides.sort((a, b) => {
      if (a.distance < b.distance) return 1;
      if (a.distance > b.distance) return -1;
      return 0;
    });
    // Fix the marker to the furthest edge, and factor in UI button locations
    let xOffsetLeft = MapboxEdgeMarker.edgeOffset + this.safeAreaInsetLeft;
    let xOffsetRight = MapboxEdgeMarker.edgeOffset + this.safeAreaInsetRight;
    let yOffsetTop = MapboxEdgeMarker.edgeOffset + this.safeAreaInsetTop;
    let yOffsetBottom = MapboxEdgeMarker.edgeOffset + this.safeAreaInsetBottom;
    if (sides[0].side === 'top') {
      xOffsetLeft += MapboxEdgeMarker.buttonOffset;
      xOffsetRight += MapboxEdgeMarker.buttonOffset;
    }
    if (sides[0].side === 'bottom') {
      xOffsetRight += MapboxEdgeMarker.buttonOffset;
    }
    if (sides[0].side === 'right') {
      yOffsetTop += MapboxEdgeMarker.buttonOffset;
      yOffsetBottom += MapboxEdgeMarker.buttonOffset;
    }
    if (sides[0].side === 'left') {
      yOffsetTop += MapboxEdgeMarker.buttonOffset;
    }
    // Add helper classes to give the marker the correct arrows to show direction
    const edgeMarkerProperties: EdgeMarkerProperties = {
      x: markerPosition.x,
      y: markerPosition.y,
      classes: [],
    };
    if (markerPosition.x < xOffsetLeft) {
      edgeMarkerProperties.x = xOffsetLeft;
      edgeMarkerProperties.classes.push('edge-marker--arrow-left');
    } else if (
      markerPosition.x >
      this.mapContainer.clientWidth - xOffsetRight
    ) {
      edgeMarkerProperties.x = this.mapContainer.clientWidth - xOffsetRight;
      edgeMarkerProperties.classes.push('edge-marker--arrow-right');
    }
    if (markerPosition.y < yOffsetTop) {
      edgeMarkerProperties.y = yOffsetTop;
      edgeMarkerProperties.classes.push('edge-marker--arrow-top');
    } else if (
      markerPosition.y >
      this.mapContainer.clientHeight - yOffsetBottom
    ) {
      edgeMarkerProperties.y = this.mapContainer.clientHeight - yOffsetBottom;
      edgeMarkerProperties.classes.push('edge-marker--arrow-bottom');
    }
    return edgeMarkerProperties;
  }

  private updateEdgeMarker(pos: Point) {
    if (!pos) return;

    const offscreen = this.isPointOffscreen(pos);
    this.edgeMarkerEl.classList.toggle('visible', offscreen);
    if (!offscreen) return;

    const edgeMarkerProperties = this.calculateEdgeMarkerProperties(pos);
    this.edgeMarkerEl.style.transform = `translate(-50%, -50%) translate(${edgeMarkerProperties.x}px, ${edgeMarkerProperties.y}px)`;
    this.edgeMarkerEl.classList.remove(
      ...[
        'edge-marker--arrow-top',
        'edge-marker--arrow-bottom',
        'edge-marker--arrow-left',
        'edge-marker--arrow-right',
      ]
    );
    this.edgeMarkerEl.classList.add(...edgeMarkerProperties.classes);
  }

  // Override internal setter for private property from base class Marker
  set _pos(_pos: Point) {
    this.updateEdgeMarker(_pos);
    this.pos = _pos;
  }

  // Override internal getter for private property from base class Marker
  get _pos(): Point {
    return this.pos;
  }

  // Override public method for adding Marker to Map
  public addTo(map: Map) {
    this.mapContainer = map.getContainer();
    super.addTo(map);
    map.getCanvasContainer().appendChild(this.edgeMarkerEl);
    return this;
  }
}
